import { db } from './firebase';
import {
  doc, setDoc, updateDoc, deleteDoc, onSnapshot,
  collection, addDoc, serverTimestamp, query, orderBy, where
} from 'firebase/firestore';

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
  { urls: 'stun:stun3.l.google.com:19302' },
];

// Create a call WITH the offer already included (so callee has it immediately)
export const createCallWithOffer = async (callerId, callerInfo, calleeId, calleeInfo, offer, isVideo = false) => {
  const callRef = doc(collection(db, 'calls'));
  await setDoc(callRef, {
    callerId,
    callerInfo: {
      name: callerInfo.displayName || callerInfo.email?.split('@')[0] || 'Usuario',
      email: callerInfo.email,
      photo: callerInfo.photoURL || null
    },
    calleeId,
    calleeInfo: {
      name: calleeInfo.name || calleeInfo.email?.split('@')[0] || 'Usuario',
      email: calleeInfo.email,
      photo: calleeInfo.photo || calleeInfo.photoURL || null
    },
    offer: { type: offer.type, sdp: offer.sdp },
    isVideo,
    status: 'ringing',
    createdAt: serverTimestamp(),
    endedAt: null
  });
  return callRef.id;
};

// Listen for incoming calls filtered by calleeId
export const subscribeToIncomingCalls = (userId, callback) => {
  const q = query(
    collection(db, 'calls'),
    where('calleeId', '==', userId),
    where('status', '==', 'ringing')
  );
  return onSnapshot(q, (snapshot) => {
    snapshot.docChanges().forEach((change) => {
      if (change.type === 'added') {
        const call = { id: change.doc.id, ...change.doc.data() };
        if (call.offer) {
          callback(call);
        }
      }
    });
  }, (error) => {
    console.error('[Calls] Listener error:', error.message);
  });
};

// Update call status
export const updateCallStatus = async (callId, status) => {
  try {
    await updateDoc(doc(db, 'calls', callId), {
      status,
      ...(status === 'ended' || status === 'missed' ? { endedAt: serverTimestamp() } : {})
    });
  } catch (e) {
    console.error('[Calls] Error updating status:', e);
  }
};

// Store WebRTC answer
export const setAnswer = async (callId, answer) => {
  await updateDoc(doc(db, 'calls', callId), {
    answer: { type: answer.type, sdp: answer.sdp }
  });
};

// Add ICE candidate
export const addIceCandidate = async (callId, candidate, role) => {
  const subcol = role === 'caller' ? 'callerCandidates' : 'calleeCandidates';
  await addDoc(collection(db, 'calls', callId, subcol), {
    ...candidate.toJSON(),
    createdAt: serverTimestamp()
  });
};

// Listen for ICE candidates
export const subscribeToIceCandidates = (callId, role, callback) => {
  const subcol = role === 'caller' ? 'calleeCandidates' : 'callerCandidates';
  return onSnapshot(
    query(collection(db, 'calls', callId, subcol), orderBy('createdAt')),
    (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          callback(change.doc.data());
        }
      });
    }
  );
};

// Listen for call updates (answer, status changes)
export const subscribeToCall = (callId, callback) => {
  return onSnapshot(doc(db, 'calls', callId), (snap) => {
    if (snap.exists()) {
      callback({ id: snap.id, ...snap.data() });
    }
  });
};

// Delete call document
export const cleanupCall = async (callId) => {
  try {
    await deleteDoc(doc(db, 'calls', callId));
  } catch (e) {
    console.error('Error cleaning up call:', e);
  }
};

export { ICE_SERVERS };
